import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Upload, Users, Building2, User, Star, TrendingUp, Filter, Phone } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const AdminCustomers = () => {
  const { adminToken } = useAdminAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('updatedAt'); // Default: latest updated
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  
  useEffect(() => {
    if (adminToken) {
      loadCustomers();
      loadStats();
      loadAgents();
    }
  }, [statusFilter, userTypeFilter, searchTerm, sortBy, adminToken]);

  const loadAgents = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/agents?is_active=true`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) setAgents((await res.json()).data || []);
    } catch (e) { console.error(e); }
  };

  const assignAgent = async (customerId, agentId) => {
    setAssigningId(customerId);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${customerId}/assign-agent`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ agentId: agentId || null }),
      });
      if (res.ok) await loadCustomers();
    } finally { setAssigningId(null); }
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (userTypeFilter) params.append('user_type', userTypeFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('limit', '100');

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setCustomers(result.data || []);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/stats`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/import-leads`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          },
          body: formData
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        loadCustomers();
        loadStats();
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Errore durante l\'upload del CSV');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      lead: { color: 'bg-blue-100 text-blue-800', icon: <TrendingUp className="w-3 h-3" />, label: 'Lead' },
      prospect: { color: 'bg-yellow-100 text-yellow-800', icon: <User className="w-3 h-3" />, label: 'Prospect' },
      customer: { color: 'bg-green-100 text-green-800', icon: <Users className="w-3 h-3" />, label: 'Cliente' },
      vip: { color: 'bg-purple-100 text-purple-800', icon: <Star className="w-3 h-3" />, label: 'VIP' }
    };

    const badge = badges[status] || badges.prospect;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestione Clienti CRM</h1>
          <p className="text-gray-600">Gestisci i tuoi clienti, lead e statistiche</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Totale Clienti</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_customers}</p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Lead</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.by_status.lead}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Prospect</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.by_status.prospect}</p>
                </div>
                <User className="w-8 h-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Clienti Attivi</p>
                  <p className="text-2xl font-bold text-green-600">{stats.by_status.customer}</p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">VIP</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.by_status.vip}</p>
                </div>
                <Star className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Cerca: nome, cognome, email, ragione sociale..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              {searchTerm && (
                <p className="text-xs text-gray-500 mt-1 ml-1">
                  Ricerca in: nome, cognome, email, ragione sociale
                </p>
              )}
            </div>

            {/* Filters */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Tutti gli stati</option>
              <option value="lead">Lead</option>
              <option value="prospect">Prospect</option>
              <option value="customer">Cliente</option>
              <option value="vip">VIP</option>
            </select>

            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Tutti i tipi</option>
              <option value="business">Business</option>
              <option value="private">Privati</option>
            </select>

            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-blue-50"
            >
              <option value="updatedAt">📅 Più recenti</option>
              <option value="createdAt">🆕 Nuovi clienti</option>
              <option value="firstName">🔤 Nome A-Z</option>
            </select>

            {/* Import CSV */}
            <label className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer flex items-center gap-2">
              <Upload className="w-5 h-5" />
              {uploading ? 'Caricamento...' : 'Importa Lead CSV'}
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Caricamento clienti...</p>
            </div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Nessun cliente trovato
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assegnato a
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Telefono
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ordini
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fatturato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ultimo Ordine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                            {customer.userType === 'business' ? (
                              <Building2 className="w-5 h-5 text-purple-600" />
                            ) : (
                              <User className="w-5 h-5 text-purple-600" />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <Link 
                            to={`/admin/customers/${customer.id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {customer.firstName} {customer.lastName}
                          </Link>
                          {customer.companyName && (
                            <div className="text-sm text-gray-600 font-medium">
                              {customer.companyName}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">{customer.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        customer.userType === 'business' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {customer.userType === 'business' ? (
                          <>
                            <Building2 className="w-3 h-3" />
                            Business
                          </>
                        ) : (
                          <>
                            <User className="w-3 h-3" />
                            Privato
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(customer.customerStatus)}
                        {customer.customerStatus === 'lead' && customer.leadSource === 'Landing Page Form' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 animate-pulse">
                            🆕 Nuovo
                          </span>
                        )}
                      </div>
                    </td>
                    
                    {/* Assegnato a */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {customer.assignedToEmail ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center">
                            <span className="text-xs font-semibold text-teal-700">
                              {customer.assignedToEmail.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-gray-700">
                            {customer.assignedToEmail.split('@')[0]}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Non assegnato</span>
                      )}
                    </td>

                    {/* Agente assegnato (Fase 2) */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm" data-testid={`agent-cell-${customer.id}`}>
                      <select
                        value={customer.assignedAgentId || ''}
                        onChange={(e) => assignAgent(customer.id, e.target.value)}
                        disabled={assigningId === customer.id}
                        data-testid={`assign-agent-select-${customer.id}`}
                        className="text-xs px-2 py-1 border border-gray-300 rounded-md bg-white hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 max-w-[160px]"
                      >
                        <option value="">— Nessuno —</option>
                        {agents.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.firstName} {a.lastName} {a.code ? `(${a.code})` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    
                    {/* Phone with WhatsApp Link */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {customer.phone ? (
                        <a
                          href={`https://wa.me/${customer.phone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-green-600 hover:text-green-800 transition-colors"
                          title="Apri chat WhatsApp"
                        >
                          <Phone className="w-4 h-4" />
                          <span>{customer.phone}</span>
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">Non disponibile</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {customer.totalOrders}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(customer.totalSpent)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(customer.lastOrderDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/admin/customers/${customer.id}`}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        Dettagli →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Info CSV Format */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">📄 Formato CSV per Import Lead</h3>
          <p className="text-sm text-blue-700 mb-2">
            Il file CSV deve contenere le seguenti colonne:
          </p>
          <code className="text-xs bg-white px-2 py-1 rounded text-blue-900 block">
            nome,cognome,email,telefono,azienda,fonte
          </code>
          <p className="text-xs text-blue-600 mt-2">
            Esempio: Mario,Rossi,mario@test.com,3331234567,Bar Mario,Meta Ads
          </p>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminCustomers;
